/**
 * Build professional HTML for team announcement emails.
 * Header/footer are trusted HTML from HR templates; title/sender are escaped.
 * Main body: if it looks like HTML, inserted as-is; otherwise escaped with line breaks.
 */

export function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function looksLikeHtmlFragment(s) {
  return /<\/?[a-z][a-z0-9]*\b/i.test(s) || /&#?\w+;/.test(s);
}

/** Main announcement body: HTML from HR, or plain text → escaped + &lt;br/&gt; */
export function normalizeAnnouncementBodyForHtml(body) {
  const t = String(body ?? '').trim();
  if (!t) return '';
  if (looksLikeHtmlFragment(t)) return t;
  return escapeHtml(t).replace(/\n/g, '<br />');
}

/** Very small plain-text fallback for multipart/alternative */
export function roughStripHtml(html) {
  return String(html ?? '')
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * @param {object} p
 * @param {string} p.sender - plain display name/email
 * @param {string} p.title - announcement subject line text
 * @param {string} p.body - main content (HTML or plain)
 * @param {string} [p.headerHtml]
 * @param {string} [p.footerHtml]
 */
export function buildProfessionalAnnouncementHtml({
  sender,
  title,
  body,
  headerHtml = '',
  footerHtml = '',
}) {
  const senderEsc = escapeHtml(sender);
  const titleEsc = escapeHtml(title);
  const mainInner = normalizeAnnouncementBodyForHtml(body);

  const mainCard = `
<table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="max-width:600px;margin:0 auto;background:#ffffff;border-radius:8px;overflow:hidden;border:1px solid #e2e8f0;box-shadow:0 1px 3px rgba(15,23,42,0.06)">
  <tr>
    <td style="padding:20px 24px 8px">
      <p style="margin:0;color:#64748b;font-size:13px;line-height:1.45">From: ${senderEsc}</p>
    </td>
  </tr>
  <tr>
    <td style="padding:4px 24px 16px">
      <h1 style="margin:0;font-size:22px;font-weight:600;line-height:1.3;color:#0f172a;font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif">${titleEsc}</h1>
    </td>
  </tr>
  <tr>
    <td style="padding:0 24px 28px;font-size:15px;line-height:1.65;color:#334155;font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif" class="ecdt-announcement-main">
      ${mainInner}
    </td>
  </tr>
</table>`;

  const headerBlock = (headerHtml || '').trim();
  const footerBlock = (footerHtml || '').trim();

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <meta http-equiv="x-ua-compatible" content="ie=edge">
  <title>${titleEsc}</title>
</head>
<body style="margin:0;padding:24px 12px;background:#f1f5f9;-webkit-font-smoothing:antialiased;">
  <div style="max-width:600px;margin:0 auto 16px">
    ${headerBlock}
  </div>
  <div style="max-width:600px;margin:0 auto">
    ${mainCard}
  </div>
  <div style="max-width:600px;margin:16px auto 0">
    ${footerBlock}
  </div>
</body>
</html>`;
}

export function buildAnnouncementPlainText({ sender, title, body, headerHtml, footerHtml }) {
  const rawBody = String(body ?? '');
  const mainText = looksLikeHtmlFragment(rawBody) ? roughStripHtml(rawBody) : rawBody.trim();
  return [
    roughStripHtml(headerHtml || ''),
    `From: ${sender}`,
    '',
    title,
    '',
    mainText,
    '',
    roughStripHtml(footerHtml || ''),
    '',
    '— ECDT Portal',
  ]
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
